import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Card, CardContent, Chip, CircularProgress, Backdrop
} from '@mui/material';
import { Add, Edit, Delete, ViewModule, ViewList } from '@mui/icons-material';

const Specialties: React.FC = () => {
  const [services, setServices] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', description: '', category: '', duration: '', price: '' });
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '' });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/services');
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/services/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingService ? `https://salon.rockyroadsolutions.com/api/services/${editingService.id}` : 'https://salon.rockyroadsolutions.com/api/services';
      const method = editingService ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        fetchServices();
        setIsFormOpen(false);
        setEditingService(null);
        setFormData({ name: '', description: '', category: '', duration: '', price: '' });
      }
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const handleCategorySubmit = async () => {
    try {
      const url = editingCategory ? `https://salon.rockyroadsolutions.com/api/services/categories/${editingCategory.id}` : 'https://salon.rockyroadsolutions.com/api/services/categories';
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData)
      });
      
      if (response.ok) {
        fetchCategories();
        setIsCategoryFormOpen(false);
        setEditingCategory(null);
        setCategoryFormData({ name: '', description: '' });
      }
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleEdit = (service: any) => {
    setEditingService(service);
    setFormData(service);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await fetch(`https://salon.rockyroadsolutions.com/api/services/${id}`, { method: 'DELETE' });
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await fetch(`https://salon.rockyroadsolutions.com/api/services/categories/${id}`, { method: 'DELETE' });
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Hair': return '#E91E63';
      case 'Makeup': return '#9C27B0';
      case 'Skin': return '#FF5722';
      case 'Nails': return '#00BCD4';
      case 'Spa': return '#4CAF50';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Services & Categories</Typography>
        <Box display="flex" gap={2}>
          <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}>
            <IconButton size="small" onClick={() => setViewMode('grid')} sx={{ color: viewMode === 'grid' ? '#E91E63' : '#666' }}>
              <ViewModule />
            </IconButton>
            <IconButton size="small" onClick={() => setViewMode('list')} sx={{ color: viewMode === 'list' ? '#E91E63' : '#666' }}>
              <ViewList />
            </IconButton>
          </Box>

        </Box>
      </Box>

      {/* Categories Section */}
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', border: '1px solid #dee2e6' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ color: '#495057', fontWeight: 600 }}>📂 Categories Management</Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setIsCategoryFormOpen(true)}>
            Add Category
          </Button>
        </Box>
        <Grid container spacing={2}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card sx={{ border: '1px solid #ddd', borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{category.name}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => { setEditingCategory(category); setCategoryFormData(category); setIsCategoryFormOpen(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteCategory(category.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{category.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Services Section - Grouped by Category */}
      <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)', border: '1px solid #E91E63' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ color: '#E91E63', fontWeight: 600 }}>✨ Services Management</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingService(null); setFormData({ name: '', description: '', category: '', duration: '', price: '' }); setIsFormOpen(true); }}>
            Add Service
          </Button>
        </Box>
      {categories.map((category) => {
        const categoryServices = services.filter(service => service.category === category.name);
        if (categoryServices.length === 0) return null;
        
        return (
          <Box key={category.id} mb={4}>
            <Typography variant="h6" gutterBottom sx={{ color: getCategoryColor(category.name), fontWeight: 600 }}>
              {category.name} Services
            </Typography>
            {viewMode === 'grid' ? (
              <Grid container spacing={3}>
                {categoryServices.map((service) => (
            <Grid item xs={12} md={6} lg={4} key={service.id}>
              <Card sx={{
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: `1px solid ${getCategoryColor(service.category)}`,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 25px ${getCategoryColor(service.category)}30` }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight={600} color={getCategoryColor(service.category)}>
                      {service.name}
                    </Typography>
                    <Chip label={service.category} sx={{ backgroundColor: getCategoryColor(service.category), color: 'white' }} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40 }}>
                    {service.description}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Box textAlign="center" sx={{ p: 1, backgroundColor: '#F3E5F5', borderRadius: 2, flex: 1, mr: 1 }}>
                      <Typography variant="body2" color="text.secondary">{service.duration} min</Typography>
                    </Box>
                    <Box textAlign="center" sx={{ p: 1, backgroundColor: '#E8F5E8', borderRadius: 2, flex: 1, ml: 1 }}>
                      <Typography variant="h6" fontWeight={700} color="#4CAF50">${service.price}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(service)} sx={{ borderColor: '#FF9800', color: '#FF9800' }}>Edit</Button>
                    <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(service.id)} sx={{ borderColor: '#F44336', color: '#F44336' }}>Delete</Button>
                  </Box>
                </CardContent>
              </Card>
                </Grid>
              ))}
            </Grid>
            ) : (
              <Box>
                {categoryServices.map((service) => (
            <Card key={service.id} sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: `1px solid ${getCategoryColor(service.category)}`,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: `0 4px 15px ${getCategoryColor(service.category)}20` }
            }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1" fontWeight={600} color={getCategoryColor(service.category)}>
                      {service.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography variant="body2" color="text.secondary">
                      {service.duration}m
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Typography variant="body1" fontWeight={600} color="#4CAF50">
                      ${service.price}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Chip label={service.category} sx={{ backgroundColor: getCategoryColor(service.category), color: 'white' }} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(service)} sx={{ borderColor: '#FF9800', color: '#FF9800', minWidth: 'auto', px: 1 }}>Edit</Button>
                      <Button size="small" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(service.id)} sx={{ borderColor: '#F44336', color: '#F44336', minWidth: 'auto', px: 1 }}>Del</Button>
                    </Box>
                  </Grid>
                </Grid>
                </CardContent>
              </Card>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
      </Paper>

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Service Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Duration (minutes)" type="number" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Price ($)" type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                select 
                fullWidth 
                label="Category" 
                value={formData.category} 
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                SelectProps={{
                  native: true,
                  displayEmpty: true
                }}
                InputLabelProps={{
                  shrink: true
                }}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingService ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCategoryFormOpen} onClose={() => setIsCategoryFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Category Name" value={categoryFormData.name} onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" value={categoryFormData.description} onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCategoryFormOpen(false)}>Cancel</Button>
          <Button onClick={handleCategorySubmit} variant="contained">{editingCategory ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Specialties;
